import { useState } from "react";
import api, { formatApiError } from "../lib/api";
import { useBrand } from "../context/BrandContext";
import { CheckCircle2 } from "lucide-react";

export default function AdvertisePage() {
    const brand = useBrand();
    const [form, setForm] = useState({ name: "", email: "", company: "", phone: "", budget: "", message: "" });
    const [done, setDone] = useState(false);
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const submit = async (e) => {
        e.preventDefault();
        setError("");
        setLoading(true);
        try {
            const { data } = await api.post("/advertise", form);
            if (data.success) setDone(true);
        } catch (err) {
            setError(formatApiError(err.response?.data?.detail) || err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="max-w-[1100px] mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="advertise-page">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-14">
                <div>
                    <span className="font-mono text-xs uppercase tracking-widest text-accent">Partnerships</span>
                    <h1 className="font-serif text-4xl sm:text-5xl font-black mt-1 leading-tight">Advertise with {brand.site_name}</h1>
                    <p className="mt-5 text-base text-muted-foreground leading-relaxed">
                        Reach an engaged audience of investors, business owners and money-minded readers.
                        Tell us about your campaign and our partnerships team will send our media kit and rates.
                    </p>
                    <div className="mt-8 space-y-4 border-t border-hair pt-8">
                        {["Premium homepage and section placements", "Newsletter sponsorships (email)", "Native and sponsored editorial", "SMS broadcast campaigns"].map((x) => (
                            <div key={x} className="flex items-center gap-3">
                                <span className="w-1.5 h-1.5 bg-accent" />
                                <span className="text-sm">{x}</span>
                            </div>
                        ))}
                    </div>
                    <p className="mt-8 font-mono text-xs text-muted-foreground">Or email us directly at {brand.email}</p>
                </div>

                <div className="border-2 border-ink p-8">
                    {done ? (
                        <div className="flex flex-col items-center text-center py-10" data-testid="advertise-success">
                            <CheckCircle2 className="text-pos" size={40} />
                            <h2 className="font-serif text-2xl font-bold mt-4">Enquiry received</h2>
                            <p className="mt-2 text-sm text-muted-foreground">Thank you. Our partnerships team will be in touch shortly.</p>
                        </div>
                    ) : (
                        <form onSubmit={submit} className="space-y-4">
                            <h2 className="font-serif text-2xl font-bold mb-2">Enquiry form</h2>
                            <div className="grid grid-cols-2 gap-4">
                                <input required placeholder="Full name" value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="advertise-name" className="border border-ink px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-accent" />
                                <input required placeholder="Company" value={form.company} onChange={(e) => set("company", e.target.value)} data-testid="advertise-company" className="border border-ink px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-accent" />
                            </div>
                            <input type="email" required placeholder="Work email" value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="advertise-email" className="w-full border border-ink px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-accent" />
                            <div className="grid grid-cols-2 gap-4">
                                <input placeholder="Phone (optional)" value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="advertise-phone" className="border border-ink px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-accent" />
                                <input placeholder="Budget (optional)" value={form.budget} onChange={(e) => set("budget", e.target.value)} data-testid="advertise-budget" className="border border-ink px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-accent" />
                            </div>
                            <textarea required rows={4} placeholder="Tell us about your campaign" value={form.message} onChange={(e) => set("message", e.target.value)} data-testid="advertise-message" className="w-full border border-ink px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-accent resize-none" />
                            {error && <p className="text-sm text-neg border border-neg p-3" data-testid="advertise-error">{error}</p>}
                            <button type="submit" disabled={loading} data-testid="advertise-submit" className="w-full bg-ink text-white py-3 font-mono text-xs uppercase tracking-widest hover:bg-accent transition-colors duration-150 disabled:opacity-50">
                                {loading ? "Sending…" : "Send enquiry"}
                            </button>
                        </form>
                    )}
                </div>
            </div>
        </div>
    );
}
